import { IonCol, IonGrid, IonRow } from "@ionic/react";
import { useEffect, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import PrimaryButton from "@components/buttons/PrimaryButton";
import SignedDocumentFaxForm from "@components/forms/SignedDocumentFaxForm";
import Modal from "@components/Modal";
import Icon from "@components/text/Icon";
import useOpener, { Opener } from "@hooks/useOpener";
import { FaxAttemptStatusEnum, GetJourneyWithSignedDocumentsQuery } from "@typing/Generated";

type Props = {
  currentFaxFormId: string | null;
  journey?: GetJourneyWithSignedDocumentsQuery["journey"];
  opener: Opener;
};
const ConsentFormFax = ({ currentFaxFormId, journey, opener }: Props) => {
  const faxFormOpener = useOpener();
  const intl = useIntl();

  const consentForm = useMemo(
    () => journey?.signedDocuments.find(sd => sd.id === currentFaxFormId),
    [currentFaxFormId, journey?.signedDocuments]
  );

  useEffect(() => {
    if (consentForm?.faxAttempts.length) {
      faxFormOpener.close();
    } else {
      faxFormOpener.open();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consentForm]);

  return (
    <Modal
      hideTitleBorder
      opener={opener}
      title={intl.formatMessage({
        id: faxFormOpener.isOpen ? "models.consentForm.fax.newFax" : "models.consentForm.fax.viewFaxes"
      })}
    >
      {({ modalDisplayed }) => (
        <div>
          {faxFormOpener.isOpen && consentForm ? (
            <SignedDocumentFaxForm
              autofocus={modalDisplayed}
              onCancel={consentForm?.faxAttempts.length ? faxFormOpener.close : opener.close}
              onSuccess={opener.close}
              signedDocument={consentForm}
            />
          ) : (
            <div>
              {consentForm?.faxAttempts.map(faxAttempt => (
                <IonGrid key={faxAttempt.id}>
                  <IonRow>
                    <IonCol>
                      <FormattedMessage id="models.consentForm.fax.to" />
                      {faxAttempt.to}
                    </IonCol>
                    <IonCol>
                      <FormattedMessage id="models.consentForm.fax.status" />
                      <FormattedMessage id={`models.consentForm.fax.statusCodes.${faxAttempt.status}`} />
                      {(faxAttempt.status === FaxAttemptStatusEnum.PENDING ||
                        faxAttempt.status === FaxAttemptStatusEnum.TRANSMITTING) && (
                        <Icon icon="spinner" size="small" spacingStart />
                      )}
                    </IonCol>
                    {faxAttempt.error && (
                      <IonCol>
                        <FormattedMessage id="models.consentForm.fax.error" />
                        <FormattedMessage id={`models.consentForm.fax.errorCodes.${faxAttempt.error}`} />
                      </IonCol>
                    )}
                  </IonRow>
                </IonGrid>
              ))}
            </div>
          )}
          {!faxFormOpener.isOpen && (
            <div className="ion-margin-top">
              <PrimaryButton onClick={faxFormOpener.open} textKey="models.consentForm.fax.sendAnotherFax" />
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default ConsentFormFax;
